import axios, { AxiosError } from "axios";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMultistepForm } from "../../../hooks/useMultistepForm";

import Stepsbar from "../../../components/Stepsbar";
import FormAkun from "./partial/FormAkun";
import FormRegistrasi from "./partial/FormRegistrasi";

import SkeletonRow from "@/components/SkeletonRow";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ArrowLeft, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { FaCheck } from "react-icons/fa6";
import { FiAlertTriangle } from "react-icons/fi";
import { IoLogIn } from "react-icons/io5";
import { Button } from "../../../components/ui/button";
import { ErrorType, InputRegistrasi, Step, User } from "../../../types";

type RegisteredData = {
  user: User | null;
  hasDataSiswa: boolean | null;
};

export default function RegistrasiPage() {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [steps, setSteps] = useState<Step[]>([
    {
      number: 1,
      text: "Account Registration",
      link: "#",
      status: "active",
    },
    {
      number: 1.5,
      text: null,
      link: null,
      status: "nonactive",
    },
    {
      number: 2,
      text: "Personal Information",
      link: "#",
      status: "nonactive",
    },
  ]);
  const [form, setForm] = useState<InputRegistrasi>({
    nama_lengkap: "",
    email: "",
    password: "",
    password_confirmation: "",
    data_siswa: {
      id_ref_negara: "103",
      nik: "",
      nisn: "",
      jenis_kelamin: "L",
      handphone: "",
      alamat: "",
    },
  });
  const [isPending, setisPending] = useState<boolean>(false);
  const [isPendingData, setisPendingData] = useState<boolean>(false);
  const [error, setError] = useState<ErrorType | null>(null);
  const [registeredData, setRegisteredData] = useState<RegisteredData>({
    user: null,
    hasDataSiswa: null,
  });
  const { firstStep, lastStep, currentStep, goToStep, currentStepNumber } =
    useMultistepForm([
      <FormAkun form={form} setForm={setForm} error={error} />,
      <FormRegistrasi form={form} setForm={setForm} error={error} />,
    ]);
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (firstStep) return handleSendEmailVerification();
    if (lastStep) return handleRegistrasiSiswa();
  };

  const handleSendEmailVerification = async () => {
    setisPending(true);
    try {
      await axios.post(`${apiBaseUrl}/registrasi/account`, {
        ...form,
        lang: localStorage.getItem("lang") ?? "id",
        tag: searchParams.get("tag"),
      });
      const tag = searchParams.get("tag");
      navigate(`/registrasi?email=${form.email}${tag ? `&tag=${tag}` : ""}`);
    } catch (error) {
      console.error(error);
      const err = error as AxiosError<{ message?: string }>;
      if (err.response?.data) {
        toast.error(
          err.response?.data?.message || "An unknown error occurred.",
          {
            duration: 10000,
          },
        );
        setError(err.response.data as ErrorType);
      } else {
        toast.error(
          err.response?.data?.message || "An unknown error occurred.",
          {
            duration: 10000,
          },
        );
        setError({
          message: "An unknown error occurred.",
          errors: {},
        });
      }
    } finally {
      setisPending(false);
    }

    checkUser();
  };

  const handleResendEmailVerification = async () => {
    setisPending(true);
    try {
      const response = await axios.post(`${apiBaseUrl}/registrasi/resend`, {
        ...form,
        email: emailParams,
        lang: localStorage.getItem("lang") ?? "id",
      });
      toast.success(response.data?.message);
    } catch (error) {
      console.error(error);
      const err = error as AxiosError<{ message?: string }>;
      if (err.response?.data) {
        toast.error(
          err.response?.data?.message || "An unknown error occurred.",
          {
            duration: 10000,
          },
        );
        setError(err.response.data as ErrorType);
      } else {
        toast.error(
          err.response?.data?.message || "An unknown error occurred.",
          {
            duration: 10000,
          },
        );
        setError({
          message: "An unknown error occurred.",
          errors: {},
        });
      }
    } finally {
      setisPending(false);
    }
  };

  const handleRegistrasiSiswa = async () => {
    setisPending(true);
    try {
      await axios.post(`${apiBaseUrl}/registrasi/siswa`, {
        email: registeredData.user?.email,
        nama_lengkap: form.nama_lengkap,
        ...form.data_siswa,
      });

      setSteps((prev) =>
        prev.map((step) => {
          return { ...step, status: "completed" };
        }),
      );
    } catch (error) {
      console.error(error);
      const err = error as AxiosError<{ message?: string }>;
      if (err.response?.data) {
        toast.error(err.response?.data?.message || "");
        setError(err.response.data as ErrorType);
      } else {
        toast.error(
          err.response?.data?.message || "An unknown error occurred.",
        );
        setError({
          message: "An unknown error occurred.",
          errors: {},
        });
      }
    } finally {
      setisPending(false);
    }

    checkUser();
  };

  const emailParams = searchParams.get("email");

  const unverifiedUser =
    registeredData.user &&
    registeredData.user?.email_verified_at === null &&
    !registeredData.hasDataSiswa;

  const isVerified =
    registeredData.user && registeredData.user?.email_verified_at !== null;

  const isRegistered = registeredData.user && registeredData.hasDataSiswa;

  const checkUser = useCallback(async () => {
    setisPendingData(true);

    try {
      const response = await axios.get(`${apiBaseUrl}/registrasi/check`, {
        params: { email: emailParams },
      });
      setRegisteredData({
        user: response.data?.user,
        hasDataSiswa: response.data?.hasDataSiswa,
      });
      setForm((prev) => ({
        ...prev,
        nama_lengkap: response.data?.user.nama_lengkap,
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setisPendingData(false);
    }
  }, [apiBaseUrl, emailParams, setRegisteredData]);

  useEffect(() => {
    if (emailParams) checkUser();
  }, [apiBaseUrl, emailParams, checkUser]);

  useEffect(() => {
    if (isVerified) {
      goToStep(2);
      const tag = searchParams.get("tag");
      navigate(
        `/registrasi?email=${emailParams}&step=${2}${tag ? `&tag=${tag}` : ""}`,
      );
      setSteps((prev) =>
        prev.map((step) => {
          if (
            (step.number && step.number < currentStepNumber) ||
            registeredData.hasDataSiswa
          ) {
            return { ...step, status: "completed" };
          } else if (step.number === currentStepNumber) {
            return { ...step, status: "active" };
          } else {
            return { ...step, status: "nonactive" };
          }
        }),
      );
    } else if (emailParams) {
      const tag = searchParams.get("tag");
      navigate(
        `/registrasi?email=${emailParams}&step=${1}${tag ? `&tag=${tag}` : ""}`,
      );
    }
  }, [
    isVerified,
    emailParams,
    navigate,
    setSteps,
    goToStep,
    currentStepNumber,
    registeredData.hasDataSiswa,
  ]);

  /**
   * Memformat string menjadi elemen React dengan Bold (*) dan Italic (_)
   * @param text - String input dari i18n atau sumber lain
   */
  const formatRichText = (text: string): ReactNode => {
    // Tahap 1: Split untuk Bold (*)
    const boldParts = text.split(/(\*.*?\*)/g);

    return boldParts.map((part, index) => {
      if (part.startsWith("*") && part.endsWith("*")) {
        const boldContent = part.slice(1, -1);
        // Tahap 2: Di dalam Bold, cek apakah ada Italic (_)
        return (
          <strong key={`bold-${index}`} className="font-bold">
            {renderItalic(boldContent)}
          </strong>
        );
      }
      // Jika bukan bold, tetap cek apakah ada Italic di teks biasa
      return renderItalic(part, `text-${index}`);
    });
  };

  // Fungsi pembantu untuk memproses Italic
  const renderItalic = (text: string, keyPrefix?: string): ReactNode => {
    const italicParts = text.split(/(_.*?_)/g);

    return italicParts.map((part, index) => {
      if (part.startsWith("_") && part.endsWith("_")) {
        return (
          <em key={`${keyPrefix}-italic-${index}`} className="italic">
            {part.slice(1, -1)}
          </em>
        );
      }
      return part;
    });
  };

  return (
    <main className="flex-1">
      <div className="mb-10">
        <div>
          <Link
            to={
              searchParams.get("tag") === "international"
                ? "/international-admission"
                : searchParams.get("tag") === "student"
                  ? "/student-exchange"
                  : "/"
            }
            className="inline-flex items-center text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors group"
          >
            <div className="p-1 rounded-md group-hover:bg-blue-50 dark:group-hover:bg-blue-900/30 transition-colors mr-2">
              <ArrowLeft className="w-4 h-4" />
            </div>
            Back
          </Link>
        </div>
      </div>
      <div className="mb-5">
        <h1 className="mb-3 text-2xl font-bold text-center text-slate-900 dark:text-white">
          {t("guest.layout.register.header")}
        </h1>
        <Alert className="border-l-2 border-l-yellow-300 bg-yellow-50/50 dark:bg-yellow-900/20 dark:border-l-yellow-500 text-slate-900 dark:text-slate-200">
          <FiAlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
          <AlertTitle>{t("guest.layout.register.header.title")}</AlertTitle>
          <AlertDescription>
            <span>
              {formatRichText(t("guest.layout.register.header.description"))}
            </span>
          </AlertDescription>
        </Alert>
      </div>
      <section className="flex lg:flex-col flex-row gap-3">
        <Stepsbar steps={steps} goToStep={() => {}} />

        <div className="mt-5 flex-1 lgmin-h-[10rem] flex justify-center items-center">
          {isPendingData ? (
            <div className="flex flex-col gap-y-3 w-full">
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </div>
          ) : unverifiedUser ? (
            <div className="border dark:border-slate-800 p-3 rounded-xl bg-white dark:bg-slate-900 w-full">
              <h1 className="mb-1 font-bold text-slate-900 dark:text-white">
                {t("guest.layout.register.sent.title")}
              </h1>
              <p className="text-muted-foreground dark:text-slate-400">
                {t("guest.layout.register.sent.description-1")}{" "}
                <span className="text-primary dark:text-blue-400">
                  {emailParams}
                </span>
                . {t("guest.layout.register.sent.description-2")}{" "}
              </p>
              {isPending ? (
                <div className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </div>
              ) : (
                <button
                  className="text-primary dark:text-blue-400 hover:text-primary/50 dark:hover:text-blue-300 duration-300"
                  onClick={handleResendEmailVerification}
                >
                  {t("guest.layout.register.sent.resend")}
                </button>
              )}
            </div>
          ) : isRegistered ? (
            <div className="lg:p-5 p-4 rounded-xl border dark:border-slate-800 bg-white dark:bg-slate-900 w-full">
              <div className="mb-5 h-10 w-10 mx-auto rounded-full bg-success flex justify-center items-center text-white">
                <FaCheck />
              </div>
              <div>
                <h1 className="mb-1 text-xl font-bold text-center text-slate-900 dark:text-white">
                  {t("guest.layout.register.success.title")} 🎉
                </h1>
                <p className="text-center text-pretty text-muted-foreground dark:text-slate-400">
                  {t("guest.layout.register.success.description")}
                </p>
                <div className="mt-5 flex justify-center">
                  <Link
                    to={
                      searchParams.get("tag")
                        ? `/login?tag=${searchParams.get("tag")}`
                        : "/login"
                    }
                  >
                    <Button variant={"default"} className="flex gap-3">
                      Log in <IoLogIn size={20} />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex-1 w-full">
              {currentStep}

              <Button
                className="mt-5 w-full flex items-center gap-3 dark:bg-blue-600 dark:hover:bg-blue-500 dark:text-white"
                disabled={isPending}
              >
                {isPending ? (
                  <div className="flex">
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Processing...
                  </div>
                ) : firstStep ? (
                  t("guest.layout.register.send")
                ) : (
                  t("guest.layout.register.submit")
                )}
              </Button>
              <br />
              <Link
                to={
                  searchParams.get("tag")
                    ? `/login?tag=${searchParams.get("tag")}`
                    : "/login"
                }
                className="flex justify-center font-medium text-muted-foreground dark:text-slate-400 text-sm hover:text-primary dark:hover:text-blue-400 duration-300"
              >
                {t("guest.layout.register.has-account")}
              </Link>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
